
import React, { useEffect, useState } from "react";
import {
  Map,
  TileLayer,
  Popup,
  CircleMarker,
  LayersControl,
  GeoJSON,
} from "react-leaflet";
import L from "leaflet";
import { useGlobal } from "reactn";
import loadData from "../../../assets/data/LoadData";
import sortByHelper from "../../../helpers/sortBy";
// eslint-disable-next-line
import Legend from "../../molecules/legend/Legend";
import * as turf from "@turf/turf";
import Control from 'react-leaflet-control';
import DevelopedBy from '../../atoms/developedBy/DevelopedBy'
import styles from "./Mapper.module.scss";

const { BaseLayer } = LayersControl;

const Mapper = (props) => {
  const { selectedState } = props;

  // *** Below is the code for the map ref if needed ***
  let mapRef = React.createRef();

  const [showData] = useGlobal("showData");
  const [showDataLookup] = useGlobal("showDataLookup");
  const [showOverlays] = useGlobal("showOverlays");
  const [, setIsLoading] = useGlobal("isLoading");
  const [, setClickName] = useGlobal("clickName")
  const [, setChildrenCovered] = useGlobal("childrenCovered")
  const [mapPoints, setMapPoints] = useState();
  const [overlays, setOverlays] = useState();
  const [showLegend, setShowLegend] = useState();
  const [layerLegend, setLayerLegend] = useState();
  const [boundingBox, setBoundingBox] = useState();
  const [showBoundaries, setShowBoundaries] = useState();
  const [, setBuildCSV] = useGlobal("buildCSV");

  const [buildCSVTest, setBuildCSVTest] = useState([]);


  delete L.Icon.Default.prototype._getIconUrl;

  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  });


  const getPopupContent = (elem, totalSchools) => {
    if (elem && elem.properties && elem.properties.category) {
      return (
        <>
          {/* The following content is displayed for existing schools */}
          <h4>Existing School Information</h4>
          <div className={styles.tooltipContainer}>
            <div className={styles.tooltipInner}>
              <div className={styles.tooltipTitle}>School Name:</div>
              <div className={styles.tooltipContent}>
                {elem.properties.schlname}
              </div>
            </div>
            <div className={styles.tooltipInner}>
              <div className={styles.tooltipTitle}>School Type:</div>
              <div className={styles.tooltipContent}>
                {elem.properties.category}
              </div>
            </div>
            <div className={styles.tooltipInner}>
              <div className={styles.tooltipTitle}>Total Teachers:</div>
              <div className={styles.tooltipContent}>
                {elem.properties.teachers_sum}
              </div>
            </div>
          </div>
          <p className={styles.explanation}>
            Information from the{" "}
            <a href="https://grid3.org/news/how-geospatial-data-can-help-solve-nigerias-educational-challenges">
              NPA
            </a>{" "}
            UBEC 2019
          </p>
        </>
      );
    } else {
      return (
        <>
          {/* The following content is displayed for optimised school locations */}
          <h4>Proposed School Location</h4>
          <div className={styles.tooltipContainer}>
            <div className={styles.tooltipInner}>
              <div className={styles.tooltipTitle}>
                Total Children within 2km:
              </div>
              <div className={styles.tooltipContent}>
                {elem.properties.children_covered2km}
              </div>
            </div>
            <div className={styles.tooltipInner}>
              <div className={styles.tooltipTitle}>School Ranking:</div>
              <div
                className={styles.tooltipContent}
              >{`${elem.properties.rank_state} out of ${totalSchools}`}</div>
            </div>
            <div className={styles.tooltipInner}>
              <div className={styles.tooltipTitle}>LGA:</div>
              <div className={styles.tooltipContent}>
              {elem.properties.name_lga}
              </div>
            </div>
          </div>
          <p className={styles.explanation}>
            Proposed schools have been calculated by assessing the GRID3 Nigeria
            Population estimates (WorldPop, 2019), and comparing with existing
            coverage rates. For more information, please look at the "About" tab
          </p>
        </>
      );
    }
  };

  const setCSV = (data) => {
    
    if(data.properties.children_covered2km){
      let tempBuildCSV = buildCSVTest;
      if (tempBuildCSV.length === 0) {
        tempBuildCSV.push([
          "LATITUDE",
          "LONGITUDE",
          "children_covered2km",
          "rank",
          "state_name",
          "LGA_name",
          "rank_state",
          "rank_lga",
        ]);
      }
      tempBuildCSV.push([
        data.geometry.coordinates[1],
        data.geometry.coordinates[0],
        data.properties.children_covered2km,
        data.properties.rank,
        data.properties.name_state,
        data.properties.name_lga,
        data.properties.rank_state,
        data.properties.rank_lga,
      ]);
      setBuildCSVTest(tempBuildCSV);
    }
  };

  useEffect(() => {
    setShowBoundaries();
    const getBoundaries = async () => {
      let boundaryURL;

      if (selectedState !== "National") {
        const processedName = selectedState.split(" ").join("_");
        boundaryURL = `states/${processedName}.geojson`;
      } else {
        boundaryURL = `nga_state.geojson`;
      }
      
      const boundaryFile = await loadData(`https://flowminder.github.io/school-optimisation-resources-nigeria/boundaries/${boundaryURL}`)
      // Defines a bounding box to change the map view. A slight buffer is added to make sure the view is not too close
      const bBox = turf.bbox(boundaryFile)
      const bBoxWidth = bBox[2] - bBox[0]
      const bBoxHeight = bBox[3] - bBox[1]
      const bboxBuffer = 0.1
      const bBoxFormatted = [[bBox[1] - (bBoxHeight)*bboxBuffer, bBox[0] - (bBoxWidth)*bboxBuffer], [bBox[3] + (bBoxHeight)*bboxBuffer, bBox[2] + (bBoxWidth)*bboxBuffer]]
      setBoundingBox(bBoxFormatted)
      return (
        <GeoJSON
          data={boundaryFile}
          opacity={1}
          weight={1}
          color={"grey"}
          fillOpacity={0.05}
        />
      );
    };

    getBoundaries().then((value) => {
      setShowBoundaries(value);
    });
  }, [selectedState]);

  useEffect(() => {
    setIsLoading(false);
    setClickName('')
  }, [mapPoints, setIsLoading, setClickName]);

  useEffect(() => {
    let loopCount = 1;
    const getMapData = async () => {
      const mapData = [];
      // eslint-disable-next-line
      await Promise.all(
        showDataLookup.map(async (elem, i) => {
          const layerFile = await loadData(elem.url);
          const layerOpacity = elem.opacity;
          mapData.push({
            layerFile: layerFile,
            layerColour: elem.layerColour,
            rangeMin: elem.minRange,
            rangeMax: elem.maxRange,
            layerOpacity: layerOpacity,
          });
        })
      );
      return mapData;
    };

    getMapData().then((value) => {
      if (!value) return;

      let totalCoverage = 0;
      let thecount = 0
    
      const markers = value.map((dataLayer, i) => {
        const markerColour = dataLayer.layerColour;
        const layerOpacity = dataLayer.layerOpacity;

        const testArray = dataLayer.layerFile.features;
        const sorted = testArray.sort(sortByHelper("rank_state"));
        const trimmed = sorted.slice(0, dataLayer.rangeMax);

        const overlayData = trimmed.map((elem, i) => {
          if(elem.properties.children_covered2km){
            thecount = thecount + 1
            const thisCoverage = parseFloat(elem.properties.children_covered2km)
            const tCoverage = parseFloat(totalCoverage) + thisCoverage
            totalCoverage = tCoverage
          }
          setCSV(elem);
          const coordArray = elem.geometry.coordinates;
          const coordlon = coordArray[0];
          const coordLat = coordArray[1];
          return (
            <CircleMarker
              center={{ lng: coordlon, lat: coordLat }}
              color={markerColour}
              radius={2}
              key={coordlon + i}
              opacity={layerOpacity}
            >
              <Popup>{getPopupContent(elem, dataLayer.rangeMax)}</Popup>
            </CircleMarker>
          );
        });
        loopCount = loopCount + 1;
        return overlayData;
      });
      const markerData = [].concat.apply([], markers);
      const layerLegend = () => {
        if (value.length > 0) {
          const legendInfo = {
            "colours" : ['#115E6E', '#AB3933', '#FCAF17', '#F26522'], 
            "labels" : ['Primary', 'Proposed Primary', 'JSS', 'ECCE'], 
            "info" : 'School Data'
          }
          return <Legend info={legendInfo} rounded={true} />;
        }
      };

      setLayerLegend(layerLegend);
      setMapPoints(markerData);
      setBuildCSV(buildCSVTest);
      setChildrenCovered(totalCoverage);

      setBuildCSVTest([])
    });
    // eslint-disable-next-line
  }, [showDataLookup]);

  useEffect(() => {
    setIsLoading(false);
    setClickName('')
    const overlays = showOverlays.map((overlay, i) => {
      return (
        <TileLayer
          attribution='&copy; <a href="https://web.flowminder.org/">Flowminder</a>'
          url={overlay.url}
          opacity={overlay.opacity}
          key={i}
          zIndex={30}
          tms="false"
        />
      );
    });
    const legend = () => {
      if (showOverlays.length > 0) {
        const legendInfo = showOverlays[0].legendInfo;
        return <Legend info={legendInfo} />;
      }
    };

    setOverlays(overlays);
    setShowLegend(legend);
  }, [showOverlays, setIsLoading, setClickName]);

  return (
    <Map zoomControl={false} ref={mapRef} bounds={boundingBox}>
      <LayersControl position="bottomright" collapsed={false} >
        <BaseLayer checked name="Streets">
          <TileLayer
            tileSize={512}
            zoomOffset={-1}
            attribution='&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>'
            url="https://api.mapbox.com/styles/v1/michaelharper/cka5a8evu0x0s1imo5ny0wugg/tiles/512/{z}/{x}/{y}?access_token=pk.eyJ1IjoibWljaGFlbGhhcnBlciIsImEiOiJjazc2ZDdjamMwMzA2M25ta2ZvenF0NTAwIn0.Taw7iRD8RSv69AFd9bGGxA"
          />
        </BaseLayer>
        <BaseLayer name="Satellite">
          <TileLayer
            attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
        </BaseLayer>
        <BaseLayer name="Terrain">
          <TileLayer
            attribution="Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ, TomTom, Intermap, iPC, USGS, FAO, NPS, NRCAN, GeoBase, Kadaster NL, Ordnance Survey, Esri Japan, METI, Esri China (Hong Kong), and the GIS User Community"
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
          />
        </BaseLayer>
      </LayersControl>
      <Control position="bottomleft" >
        <DevelopedBy />
      </Control>
      {overlays && overlays}
      {showBoundaries && showBoundaries}
      {showData && mapPoints}
      {layerLegend && layerLegend}
      {showLegend && showLegend}
    </Map>
  );
};

export default Mapper;
